import React, { useState, useEffect } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import {
  Grid,
  Box,
  Paper,
  Typography,
  Button,
  Modal,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { frFR, esES } from "@mui/x-data-grid/locales";
import DeleteIcon from "@mui/icons-material/Delete";
import useAxiosPrivate from "../utils/useAxiosPrivate";
import { useAuth } from "../utils/AuthProvider";
import { canManageDistributors } from "../utils/permissions";
import muiLocalizationsMap from "../translationUtils/muiLocalizations";
import i18n from "../translationUtils/i18n";
import RegionGroups from "../components/RegionGroups";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: "40%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const localizationPicker = () => {
  if (i18n.locale === "fr-FR") {
    return frFR.components.MuiDataGrid.defaultProps.localeText;
  } else if (i18n.locale === "es-ES") {
    return esES.components.MuiDataGrid.defaultProps.localeText;
  }
  return null;
};

function DistributorRow(props) {
  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { row } = props;
  const [rows, setRows] = useState([]);
  const [toRevoke, setToRevoke] = useState(null);

  const canManage = canManageDistributors(cookies.role);

  const fetchData = () => {
    const controller = new AbortController();

    const getDistributors = async () => {
      try {
        const response = await axiosPrivate.get(
          "/api/Site/distributors/" + row.cmId,
          { signal: controller.signal }
        );
        setRows(
          response.data.map((d) => ({
            id: d.assignedDistributorId,
            userId: d.userId,
            fullName: d.fullName,
            email: d.email,
            cmId: d.cmId,
            isAdminDistributor: d.isAdminDistributor,
          }))
        );
      } catch (err) {
        if (!controller.signal.aborted) alert(err);
      }
    };

    getDistributors();

    return () => {
      controller.abort();
    };
  };

  useEffect(() => {
    return fetchData();
    // eslint-disable-next-line
  }, [props.refreshToken]);

  const handleConfirmRevoke = () => {
    if (toRevoke) {
      axiosPrivate
        .post("/api/Site/revokeDistributor", {
          userId: toRevoke.userId,
          cmId: row.cmId,
        })
        .then(() => {
          setToRevoke(null);
          fetchData();
        })
        .catch((error) => {
          console.error(error);
          alert(i18n.t("errorrevokingdistributor"));
          setToRevoke(null);
        });
    }
  };

  const columns = [
    { field: "fullName", headerName: i18n.t("name"), flex: 1, minWidth: 130 },
    { field: "email", headerName: i18n.t("email"), flex: 1.4, minWidth: 160 },
  ];

  if (canManage) {
    columns.push({
      field: "action",
      headerName: i18n.t("actions"),
      width: 90,
      sortable: false,
      renderCell: (params) =>
        params.row.isAdminDistributor ? null : (
          <Tooltip title={i18n.t("revokedistributor")}>
            <IconButton
              aria-label="revoke"
              color="primary"
              onClick={() => setToRevoke(params.row)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
    });
  }

  return (
    <>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={7}>
          <Paper sx={{ height: 350 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              sx={{ border: 0 }}
              localeText={localizationPicker()}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <RegionGroups cmId={row.cmId} />
        </Grid>
      </Grid>
      <Dialog open={toRevoke !== null} onClose={() => setToRevoke(null)}>
        <DialogTitle>{i18n.t("revokedistributortitle")}</DialogTitle>
        <DialogContent>
          {toRevoke &&
            i18n.t("revokedistributortip", {
              name: toRevoke.fullName || toRevoke.email,
              master: row.cmName || row.cmId,
            })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToRevoke(null)} color="primary">
            {i18n.t("close")}
          </Button>
          <Button onClick={handleConfirmRevoke} color="primary">
            {i18n.t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function Distributors() {
  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [refreshTokens, setRefreshTokens] = useState({});
  const [inviteCm, setInviteCm] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [validEmail, setValidEmail] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);

  const canManage = canManageDistributors(cookies.role);

  const handleOpen = (cmId) => {
    setInviteCm(cmId);
    setOpen(true);
  };
  const handleClose = () => {
    setInviteCm(null);
    setInviteEmail("");
    setEmailFocus(false);
    setOpen(false);
  };

  const fetchData = () => {
    const controller = new AbortController();

    const getCustomerMasters = async () => {
      try {
        const response = await axiosPrivate.get("/api/Site/customerMasters", {
          signal: controller.signal,
        });
        setRows(
          response.data
            .filter((cm) => cm.cmName && cm.cmName.trim() !== "")
            .map((cm) => ({
              cmId: cm.cmId,
              cmName: cm.cmName,
              location: [cm.cmCity, cm.cmState].filter(Boolean).join(", "),
              cmAccountManager: cm.cmAccountManager,
            }))
        );
      } catch (err) {
        if (!controller.signal.aborted) alert(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    getCustomerMasters();

    return () => {
      controller.abort();
    };
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setValidEmail(EMAIL_REGEX.test(String(inviteEmail).toLowerCase()));
  }, [inviteEmail]);

  const handleSendInvite = () => {
    axiosPrivate
      .post("/api/Site/inviteDistributor", {
        email: inviteEmail.trim(),
        cmId: inviteCm,
      })
      .then((response) => {
        alert(response.data);
        if (String(response.data).toLowerCase().includes("success")) {
          setRefreshTokens((prev) => ({
            ...prev,
            [inviteCm]: (prev[inviteCm] || 0) + 1,
          }));
          handleClose();
        }
      })
      .catch((error) => {
        console.error(error);
        alert(i18n.t("failedaddingdistributor"));
      });
  };

  const columns = [
    {
      accessorKey: "cmName",
      header: i18n.t("customermaster"),
      enableColumnOrdering: false,
      minSize: 220,
    },
    {
      accessorKey: "location",
      header: i18n.t("location"),
      minSize: 150,
    },
    {
      accessorKey: "cmAccountManager",
      header: i18n.t("accountmanager"),
      enableColumnOrdering: false,
      minSize: 150,
    },
  ];

  if (canManage) {
    columns.push({
      accessorKey: "cmId",
      header: "",
      size: 150,
      Cell: ({ row }) => (
        <Button
          variant="contained"
          onClick={() => handleOpen(row.original.cmId)}
          sx={{ padding: "3px" }}
        >
          {i18n.t("invitedistributor")}
        </Button>
      ),
    });
  }

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: muiLocalizationsMap,
    enableExpandAll: true,
    enableColumnOrdering: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    paginationDisplayMode: "pages",
    layoutMode: "grid",
    initialState: {
      columnPinning: { left: ["mrt-row-expand", "cmName"] },
      pagination: { pageSize: 100, pageIndex: 0 },
    },
    state: {
      showSkeletons: loading,
    },
    muiPaginationProps: {
      shape: "rounded",
      rowsPerPageOptions: [50, 100, 200],
      showFirstButton: true,
      showLastButton: true,
    },
    muiTableContainerProps: { sx: { maxHeight: "70vh" } },
    muiExpandButtonProps: ({ row }) => ({
      sx: {
        transform: row.getIsExpanded() ? "rotate(180deg)" : "rotate(-90deg)",
        transition: "transform 0.2s",
      },
    }),
    renderDetailPanel: ({ row }) => (
      <DistributorRow
        key={row.original.cmId}
        row={row.original}
        refreshToken={refreshTokens[row.original.cmId] || 0}
      />
    ),
  });

  return (
    <>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ paddingTop: "1%", marginBottom: "1%" }}
      >
        <Typography variant="h6" fontFamily="monospace">
          {i18n.t("distributormanagement")}
        </Typography>
      </Grid>

      <MaterialReactTable table={table} />

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography variant="h6" component="h2" style={{ paddingBottom: "20px" }}>
            {i18n.t("invitedistributor")}
          </Typography>

          <TextField
            label={i18n.t("email")}
            fullWidth
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            error={!validEmail && emailFocus && inviteEmail.length > 0}
            helperText={
              !validEmail && emailFocus && inviteEmail.length > 0
                ? i18n.t("emailisinvalid")
                : ""
            }
            onFocus={() => setEmailFocus(true)}
            sx={{ marginBottom: 2 }}
          />

          <Grid item xs={12} style={{ paddingTop: "10px" }}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                disabled={!validEmail}
                onClick={handleSendInvite}
              >
                {i18n.t("send")}
              </Button>
            </Box>
          </Grid>
        </Box>
      </Modal>
    </>
  );
}
